import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { FileText, BookOpen, TrendingUp, Briefcase, CheckCircle } from 'lucide-react';
export function ServicesPage() {
  const services = [{
    id: 'review',
    title: 'Credit Profile Review & Analysis',
    icon: FileText,
    description: 'A deep dive into your credit reports from major bureaus to ensure accuracy and identify areas for improvement.',
    included: ['Line-by-line report analysis', 'Identification of reporting errors', 'Score factor breakdown', 'Utilization ratio assessment'],
    idealFor: 'Individuals who are unsure why their score is low or suspect errors on their report.',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=600&fit=crop'
  }, {
    id: 'education',
    title: 'Credit Education & Planning',
    icon: BookOpen,
    description: 'Comprehensive education on how credit works, tailored to your specific situation and goals.',
    included: ['Understanding scoring models (FICO vs Vantage)', 'Strategies to optimize credit mix', 'Payment history best practices', 'Long-term score building roadmap'],
    idealFor: 'Anyone looking to build or rebuild credit responsibly with expert guidance.',
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&h=600&fit=crop'
  }, {
    id: 'habits',
    title: 'Financial Habits & Utilization',
    icon: TrendingUp,
    description: 'Consulting focused on behavioral changes and financial habits that impact your credit profile.',
    included: ['Budgeting for debt reduction', 'Credit card utilization strategies', 'Savings automation advice', 'Debt-to-income ratio planning'],
    idealFor: 'Those struggling with debt management or high utilization.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'
  }, {
    id: 'business',
    title: 'Business Credit Readiness',
    icon: Briefcase,
    description: 'Guidance for entrepreneurs looking to establish and build business credit separate from personal finances.',
    included: ['Entity structure basics', 'Vendor trade line strategy', 'Business credit tier explanation', 'Separation of personal/business assets'],
    idealFor: 'Small business owners and aspiring entrepreneurs.',
    image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&h=600&fit=crop'
  }];
  return <Layout data-id="element-669">
      <div className="bg-slate-50 py-16 border-b border-slate-200" data-id="element-670">
        <Container data-id="element-671">
          <div className="max-w-3xl" data-id="element-672">
            <h1 className="text-4xl font-bold text-slate-900 mb-6" data-id="element-673">
              Our Services
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed" data-id="element-674">
              We offer professional consulting and education to help you
              navigate the complex world of credit and finance. Our approach is
              transparent, compliant, and focused on your long-term empowerment.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-20" data-id="element-675">
        <div className="space-y-20" data-id="element-676">
          {services.map((service, index) => <div key={service.id} className={`flex flex-col md:flex-row gap-12 items-start ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-677">
              <div className="flex-1" data-id="element-678">
                <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-xl mb-6" data-id="element-679">
                  <service.icon className="h-8 w-8 text-blue-600" data-id="element-680" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-681">
                  {service.title}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-682">
                  {service.description}
                </p>

                <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 mb-8" data-id="element-683">
                  <h3 className="font-semibold text-slate-900 mb-4" data-id="element-684">
                    What's Included:
                  </h3>
                  <ul className="space-y-3" data-id="element-685">
                    {service.included.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-686">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-687" />
                        <span className="text-slate-700" data-id="element-688">{item}</span>
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-689">
                  <h3 className="font-semibold text-slate-900 mb-2" data-id="element-690">
                    Ideal For:
                  </h3>
                  <p className="text-slate-600 mb-8" data-id="element-691">{service.idealFor}</p>
                </div>

                <Link to="/contact" data-id="element-692">
                  <Button size="lg" data-id="element-693">Request Consultation</Button>
                </Link>
              </div>

              <div className="flex-1 w-full" data-id="element-694">
                <div className="relative h-full min-h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-695">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-696" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" data-id="element-697"></div>
                </div>
              </div>
            </div>)}
        </div>
      </Container>

      <section className="bg-slate-900 text-white py-16" data-id="element-698">
        <Container className="text-center" data-id="element-699">
          <h2 className="text-2xl font-bold mb-4" data-id="element-700">
            Not sure which service you need?
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto" data-id="element-701">
            Schedule a general consultation and we'll help you determine the
            best path forward based on your unique situation.
          </p>
          <Link to="/contact" data-id="element-702">
            <Button variant="primary" className="bg-white text-slate-900 hover:bg-slate-100" data-id="element-703">
              Talk to a Specialist
            </Button>
          </Link>
        </Container>
      </section>
    </Layout>;
}